// Shapes returned by lidz_back's /admin/integrations routes

export type JsonObject = Record<string, unknown>;

export interface IntegrationCredential {
  id: number;
  provider: string;
  label: string;
  settings: JsonObject;
  /** Write-only: the API only says which keys are set, e.g. { 'clients.apiKey': true } */
  secrets?: Record<string, true>;
  createdAt?: string;
  updatedAt?: string;
}

export const MAPPING_DIRECTIONS = ['both', 'in', 'out'] as const;
export type MappingDirection = (typeof MAPPING_DIRECTIONS)[number];

export interface IntegrationMapping {
  id: number;
  integrationId: number;
  kind: string;
  lidzProjectId: number;
  externalId: string;
  direction: MappingDirection;
  note: string | null;
}

/** IntegrationProcess.lastResult as lidz_back's runProcess writes it, for real runs and dry runs alike */
export interface LastRunResult {
  status?: 'ok' | 'error';
  dryRun?: boolean;
  triggerType?: 'tick' | 'manual' | 'webhook';
  finishedAt?: string;
  durationMs?: number;
  stats?: Record<string, number>;
  error?: string;
}

export interface IntegrationProcess {
  id: number;
  integrationId: number;
  type: string;
  enabled: boolean;
  cron: string | null;
  runningSince: string | null;
  syncedUntil: string | null;
  lastResult: LastRunResult | null;
  updatedAt: string;
}

export interface Integration {
  id: number;
  projectId: number;
  provider: string;
  label: string;
  credentialId: number | null;
  externalAccountId: string | null;
  webhookSlug: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface IntegrationDetail extends Integration {
  credential: Omit<IntegrationCredential, 'secrets'> | null;
  processes: IntegrationProcess[];
  mappings: IntegrationMapping[];
  webhookBaseUrl: string | null; // Public domain external systems post to; null when the backend has none set
}

export interface CreateIntegrationBody {
  projectId: number;
  provider: string;
  label: string;
  credentialId: number | null;
  externalAccountId: string | null;
}

export type UpdateIntegrationBody = Partial<Pick<Integration, 'label' | 'credentialId' | 'externalAccountId'>>;

export interface CreateCredentialBody {
  provider: string;
  label: string;
  settings: JsonObject;
  secrets: JsonObject;
}

export interface UpdateCredentialBody {
  settings?: JsonObject;
  secrets?: JsonObject;
}

export type MappingBody = Pick<IntegrationMapping, 'kind' | 'lidzProjectId' | 'externalId' | 'direction' | 'note'>;

type ProcessKind = 'scheduled' | 'webhook' | 'tool';

interface ProviderCatalogEntry {
  mappingKinds: string[];
  processTypes: Record<string, ProcessKind>;
  /** Prefilled in the credential form so nobody has to remember the schema */
  settingsExample: JsonObject;
  secretsExample: JsonObject;
  /** Shown under the credential form: optional keys only some companies use, which the examples leave out */
  credentialHint?: string;
  /** Prefilled in the webhook test, per resource: what the external system posts */
  webhookPayloadExamples?: Record<string, JsonObject>;
}

/*
Mirror of lidz_back's src/integrations/registry.ts, which no route exposes. The API validates everything again, so a
stale entry here only means a select misses an option: add the provider here when it lands in the registry.
*/
export const PROVIDER_CATALOG: Record<string, ProviderCatalogEntry> = {
  sci: {
    mappingKinds: ['project_name', 'project_id'],
    processTypes: {
      'clients.update': 'scheduled',
      'stock.update': 'scheduled',
      'prospects.webhook': 'webhook',
      'quote.send': 'tool',
    },
    settingsExample: { baseUrl: 'https://<prefijo>.rvc.cl', stockApi: 'standard' },
    secretsExample: { clients: { apiKey: '' } },
    credentialHint:
      'Aconcagua lee el stock de otra API: en settings, "stockApi": "aconcagua" y "stockBaseUrl" con el host ' +
      'del stock; en secrets, "stock": { "apiKey": "..." } con la key del stock.',
    webhookPayloadExamples: {
      prospects: {
        prospectId: 1,
        opportunityId: 1,
        name: 'Prueba',
        lastName: 'Lidz',
        email: 'prueba@example.com',
        phone: '+56900000000',
        rut: '11111111-1',
        projectName: '<nombre del proyecto en SCI>',
      },
    },
  },
  hubspot: {
    mappingKinds: ['deal_group'],
    processTypes: { 'clients.fetch': 'scheduled', 'clients.update': 'scheduled', 'deals.update': 'scheduled' },
    settingsExample: {},
    secretsExample: { api: { accessToken: '' } },
  },
};

export const PROVIDER_KEYS = Object.keys(PROVIDER_CATALOG);

export const getProcessKind = (provider: string, type: string): ProcessKind | null =>
  PROVIDER_CATALOG[provider]?.processTypes[type] ?? null;

/** The resources a provider receives webhooks for: prospects.webhook is POST /webhooks/<slug>/prospects */
export const getWebhookResources = (provider: string): string[] =>
  Object.entries(PROVIDER_CATALOG[provider]?.processTypes ?? {})
    .filter(([, kind]) => kind === 'webhook')
    .map(([type]) => type.replace(/\.webhook$/, ''));

// The credential section the webhook route reads: with it, a call needs the Bearer token
export const WEBHOOK_TOKEN_SECRET = 'webhook.tokenHash';

export interface WebhookSlugResponse {
  integrationId: number;
  webhookSlug: string;
  webhookPaths: string[];
}

export interface WebhookTokenResponse {
  credentialId: number;
  /** Shown once: only its hash is stored */
  token: string;
}

/** One key of a provider's config schema, as GET /admin/integrations/:id/config describes it */
export type ConfigField =
  | { key: string; description: string | null; kind: 'object'; fields: ConfigField[] }
  | { key: string; description: string | null; kind: 'boolean' | 'number' | 'string' | 'json' }
  | { key: string; description: string | null; kind: 'enum'; options: string[] };

/** One layer of the config rule: the master's or a subproject's override */
export interface ConfigLayer {
  projectId: number;
  ruleId: number | null;
  /** Only the keys this layer overrides */
  value: JsonObject;
  /** The config a run gets from this layer, or null when it does not pass the schema (see error) */
  resolved: JsonObject | null;
  error: string | null;
}

export interface IntegrationConfig {
  ruleName: string;
  /** Top-level keys runs read from the master only: a subproject override can't set them */
  masterOnlyKeys: string[];
  fields: ConfigField[];
  /** What a company without rules runs on; null when the schema has required keys */
  defaults: JsonObject | null;
  master: ConfigLayer;
  subProjects: ConfigLayer[];
}

export interface UpdateConfigBody {
  /** null writes the master's layer */
  subProjectId: number | null;
  /** An empty value deletes the layer */
  value: JsonObject;
}
